import type { StrategistTransition } from '@/entities/strategist';

// Общие хелперы влияния (lift) — единый источник в shared, без дублирования на странице.
export { liftColorClass, formatLiftPp } from '@/shared/lib/utils';

/**
 * Фиксированный порог надёжности перехода по числу диалогов `n`.
 * Переходы с `n < RELIABLE_THRESHOLD` считаются статистически ненадёжными
 * и помечаются в интерфейсе (muted + значок), независимо от текущего `minDialogs`.
 */
export const RELIABLE_THRESHOLD = 3;

/** Дефолтное значение слайдера порога надёжности (`minDialogs`), уходит в тело запроса. */
export const DEFAULT_MIN_DIALOGS = 3;

/** Режим сортировки списков/лент переходов. */
export type TransitionSort = 'lift' | 'n';

/**
 * Фильтр влияния переходов на целевой исход:
 * - `all` — без фильтра;
 * - `up` — только повышающие (lift > 0);
 * - `down` — только понижающие (lift < 0).
 */
export type InfluenceFilter = 'all' | 'up' | 'down';

/**
 * Оставляет переходы по знаку влияния (lift) согласно фильтру {@link InfluenceFilter}.
 * Возвращает новый массив, исходный не мутируется.
 */
export const filterByInfluence = (
  transitions: StrategistTransition[],
  influence: InfluenceFilter,
): StrategistTransition[] => {
  if (influence === 'up') return transitions.filter((tr) => (tr.lift ?? 0) > 0);
  if (influence === 'down') return transitions.filter((tr) => (tr.lift ?? 0) < 0);
  return transitions;
};

/** Подпись концепта с запасным значением, если label не пришёл. */
export const conceptLabel = (label: string | null, fallback: string): string =>
  label && label.trim() ? label : fallback;

/**
 * Сортирует переходы по выбранному режиму (по убыванию lift или по убыванию n).
 * Возвращает новый массив, исходный не мутируется.
 */
export const sortTransitions = (transitions: StrategistTransition[], sort: TransitionSort): StrategistTransition[] => {
  const list = [...transitions];
  if (sort === 'n') {
    list.sort((a, b) => b.n - a.n || (b.lift ?? 0) - (a.lift ?? 0));
  } else {
    list.sort((a, b) => (b.lift ?? 0) - (a.lift ?? 0) || b.n - a.n);
  }
  return list;
};

/** Обрезает длинную подпись до `max` символов с многоточием. */
export const truncate = (text: string, max: number): string =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text;
